import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const PartyLedgerReport = () => {
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Debounced states
  const [debouncedBuyer, setDebouncedBuyer] = useState('');
  const [debouncedDateFrom, setDebouncedDateFrom] = useState('');
  const [debouncedDateTo, setDebouncedDateTo] = useState('');

  // Fetch buyers on component mount
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/report/party-ledger-buyers`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setBuyers(data.data);
        } else {
          toast.error(data.error || 'Failed to load buyers');
        }
      } catch (error) {
        console.error('Error fetching buyers:', error);
        toast.error('Failed to load buyers. Please check your connection.');
      }
    };
    fetchBuyers();
  }, []);

  // Debounce buyer selection
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedBuyer(selectedBuyer);
    }, 400);
    return () => clearTimeout(handler);
  }, [selectedBuyer]);

  // Debounce date from
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDateFrom(dateFrom);
    }, 400);
    return () => clearTimeout(handler);
  }, [dateFrom]);

  // Debounce date to
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDateTo(dateTo);
    }, 400);
    return () => clearTimeout(handler);
  }, [dateTo]);

  // Auto-fetch report when debounced values change
  // Auto-fetch report when debounced values change
useEffect(() => {
  const fetchReport = async () => {
    // Only fetch if buyer is selected
    if (!debouncedBuyer) {
      setReportData(null);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        buyer_id: debouncedBuyer
      });

      // Add date filters if provided (independent)
      if (debouncedDateFrom) {
        params.append('date_from', debouncedDateFrom);
      }
      if (debouncedDateTo) {
        params.append('date_to', debouncedDateTo);
      }

      // Add flag to indicate we want unfiltered balance calculation
      params.append('calculate_balance_unfiltered', 'true');

      const response = await fetch(`${API_BASE_URL}/api/report/party-ledger?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
      } else {
        toast.error(data.error || 'Failed to generate report');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Server Error');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  fetchReport();
}, [debouncedBuyer, debouncedDateFrom, debouncedDateTo]);

  const handlePrint = () => {
    window.print();
  };

  const handleResetFilters = () => {
    setSelectedBuyer('');
    setDateFrom('');
    setDateTo('');
    setReportData(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print-container {
            width: 100%;
            max-width: 210mm;
            margin: 0 auto;
          }
          
          table {
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group;
          }
          
          tfoot {
            display: table-footer-group;
          }
          
          .print-header {
            margin-bottom: 10px;
          }
          
          .print-table {
            font-size: 10pt;
          }
          
          .print-table th,
          .print-table td {
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2 print:hidden">
          <h2 className="text-xl font-semibold">Party Ledger Report</h2>
          <div className="flex gap-1">
            <button
              onClick={handlePrint}
              disabled={!reportData}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Print Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 print:hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Name of the Buyer <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={buyers}
                value={selectedBuyer}
                onChange={setSelectedBuyer}
                placeholder="Select Buyer"
                displayKey="name"
                valueKey="id"
                required={true}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Transaction Date From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Transaction Date To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleResetFilters}
              disabled={!selectedBuyer && !dateFrom && !dateTo}
              className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset All Filters
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
        )}

        {/* No Buyer Selected Message */}
        {!loading && !selectedBuyer && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Please select a buyer to view the report
          </div>
        )}

        {/* Report Content */}
        {!loading && reportData && (
          <div className="print-container">
            <div className="bg-white border rounded border-gray-200 overflow-hidden">
              {/* Report Header */}
              <div className="bg-gray-100 p-3 border-b print-header">
                <h3 className="text-lg font-bold text-center mb-2">
                  Party Ledger Report
                </h3>
                <div className="text-xs space-y-1">
                  <div>
                    <strong>Name of the Buyer:</strong> {reportData.buyer.name}
                  </div>
                  <div>
                    <strong>Report Date:</strong> {new Date().toLocaleDateString('en-IN')}
                  </div>
                  {(dateFrom || dateTo) && (
                    <div>
                      <strong>Period:</strong>{' '}
                      {dateFrom ? formatDateDDMMYY(dateFrom) : 'Beginning'} to{' '}
                      {dateTo ? formatDateDDMMYY(dateTo) : 'Present'}
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm print-table">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        Date of Transaction
                      </th>
                      <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        Particulars
                      </th>
                      <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                        Debit
                      </th>
                      <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                        Credit
                      </th>
                      <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.transactions.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-3 py-4 text-center text-gray-500">
                          No transactions found for this buyer in the selected period
                        </td>
                      </tr>
                    ) : (
                      reportData.transactions.map((transaction, index) => {
                        const isOpeningBalance = transaction.transaction_type === 'OPENING_BALANCE';
                        return (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {isOpeningBalance ? (transaction.transaction_date ? formatDateDDMMYY(transaction.transaction_date) : '-') : (!transaction.transaction_date ? '-' : formatDateDDMMYY(transaction.transaction_date))}
                            </td>
                            <td className="px-3 py-2">
                              {isOpeningBalance ? '-' : (() => {
                                let particulars = transaction.particulars || '-';
                                // When value is negative (debit_amount > 0 for PAYMENT), show "Payment Made" instead of "Payment Received"
                                if (transaction.transaction_type === 'PAYMENT' && parseFloat(transaction.debit_amount || 0) > 0) {
                                  particulars = particulars.replace(/^Payment Received\s*/, 'Payment Made ');
                                }
                                return particulars;
                              })()}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {isOpeningBalance ? '-' : (parseFloat(transaction.debit_amount || 0) !== 0 ? formatCurrency(parseFloat(transaction.debit_amount)) : '-')}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {isOpeningBalance ? '-' : (parseFloat(transaction.credit_amount || 0) !== 0 ? formatCurrency(parseFloat(transaction.credit_amount)) : '-')}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                              {formatCurrency(transaction.balance)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {reportData.transactions.length > 0 && (
                    <tfoot className="bg-gray-100">
                      <tr>
                        <td className="px-3 py-2 font-semibold" colSpan="2">
                          Total
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {formatCurrency(reportData.summary.total_debits)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {formatCurrency(reportData.summary.total_credits)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {formatCurrency(reportData.summary.final_balance)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        <ToastContainer 
          position="top-right" 
          autoClose={3000} 
          hideProgressBar={false} 
          newestOnTop 
          closeOnClick 
          pauseOnFocusLoss 
          draggable 
          pauseOnHover 
        />
      </div>
    </>
  );
};

export default PartyLedgerReport;
